import React, { useState, useEffect } from 'react';
import { useProject } from '../contexts/ProjectContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { CheckSquare, AlertTriangle, CheckCircle, XCircle, Loader2, Clock } from 'lucide-react';

const Approvals = () => {
  const { currentProjectId, projectInfo } = useProject();
  const { api, isManager } = useAuth();
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewNotes, setReviewNotes] = useState({});
  const [processing, setProcessing] = useState({});

  const fetchApprovals = async () => {
    try {
      const data = await api(`/api/approvals/?project_id=${currentProjectId}`);
      setApprovals(data);
    } catch (err) {
      console.error('Failed to fetch approvals:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentProjectId) fetchApprovals();
  }, [currentProjectId]);

  const handleAction = async (approvalId, action) => {
    setProcessing(prev => ({ ...prev, [approvalId]: true }));
    try {
      await api(`/api/approvals/${approvalId}/${action}`, {
        method: 'POST',
        body: JSON.stringify({ notes: reviewNotes[approvalId] || '' })
      });
      toast.success(`Request ${action}d`);
      fetchApprovals();
    } catch (err) {
      toast.error(`Failed to ${action}`);
    } finally {
      setProcessing(prev => ({ ...prev, [approvalId]: false }));
    }
  };

  const getTypeIcon = (type) => {
    if (type.includes('bulk') || type.includes('outreach')) return '📤';
    if (type.includes('image') || type.includes('video')) return '🎨';
    if (type.includes('website') || type.includes('fix')) return '🔧';
    if (type.includes('budget') || type.includes('cost')) return '💰';
    return '⚠️';
  };

  return (
    <div data-testid="approvals-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">Approvals</h1>
          <p className="text-muted-foreground">Review and approve agent actions</p>
        </div>
        <Badge variant="outline" className="text-lg px-4 py-1">
          {approvals.length} pending
        </Badge>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      ) : approvals.length === 0 ? (
        <Card className="bg-card border-border card-glow">
          <CardContent className="py-16 text-center">
            <CheckCircle className="w-16 h-16 mx-auto text-emerald-400 mb-4" />
            <h3 className="text-lg font-semibold mb-2">All caught up!</h3>
            <p className="text-muted-foreground">No pending approvals</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {approvals.map((approval) => (
            <Card 
              key={approval.approval_id}
              className="approval-card bg-card border-border card-glow"
              data-testid={`approval-${approval.approval_id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="text-3xl">{getTypeIcon(approval.approval_type)}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold">{approval.title}</h3>
                      <Badge variant="outline" className="text-xs">{approval.approval_type}</Badge>
                      {approval.estimated_cost > 0 && (
                        <Badge className="bg-amber-500/20 text-amber-400">
                          Est. ${approval.estimated_cost.toFixed(2)}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">{approval.description}</p>
                    
                    {approval.action_data && Object.keys(approval.action_data).length > 0 && (
                      <pre className="text-xs bg-black/30 p-3 rounded mb-4 overflow-auto max-h-32">
                        {JSON.stringify(approval.action_data, null, 2)}
                      </pre>
                    )}

                    {isManager && (
                      <div className="space-y-3">
                        <Textarea
                          placeholder="Review notes (optional)"
                          value={reviewNotes[approval.approval_id] || ''}
                          onChange={(e) => setReviewNotes(prev => ({ ...prev, [approval.approval_id]: e.target.value }))}
                          rows={2}
                          data-testid={`notes-${approval.approval_id}`}
                        />
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleAction(approval.approval_id, 'approve')}
                            disabled={processing[approval.approval_id]}
                            data-testid={`approve-${approval.approval_id}`}
                          >
                            {processing[approval.approval_id] ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <><CheckCircle className="w-4 h-4 mr-1" /> Approve</>
                            )}
                          </Button>
                          <Button
                            variant="destructive"
                            onClick={() => handleAction(approval.approval_id, 'reject')}
                            disabled={processing[approval.approval_id]}
                            data-testid={`reject-${approval.approval_id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {new Date(approval.created_at).toLocaleString()}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Approvals;
